import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, TextField, Divider, Alert, CircularProgress, Grid, Card, CardContent } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { database } from '../firebase';
import { ref, get } from 'firebase/database';

const SubscriptionDebugger: React.FC = () => {
  const { currentUser } = useAuth();
  const { isPremium, subscriptionDetails, checkSubscription } = useSubscription();
  const [userId, setUserId] = useState<string>(currentUser?.uid || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);

  // Function to fetch user subscription data
  const fetchUserData = async (uid: string) => {
    if (!uid) {
      setError('Please enter a user ID');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        setUserData(snapshot.val());
      } else {
        setError('User not found');
        setUserData(null);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Error fetching user data');
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch current user's data initially
  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserData(currentUser.uid);
    }
  }, [currentUser]);

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto', py: 2 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Debugger
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Admin tool for debugging subscription status and data
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Current User Status
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <strong>User ID:</strong> {currentUser?.uid || 'Not logged in'}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {currentUser?.email || 'N/A'}
          </Typography>
          <Typography variant="body1">
            <strong>Premium Status:</strong> {isPremium ? 'Premium' : 'Free'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Subscription Data
        </Typography>
        {subscriptionDetails ? (
          <Box component="pre" sx={{ 
            p: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem'
          }}>
            {JSON.stringify(subscriptionDetails, null, 2)}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No subscription data available
          </Typography>
        )}

        <Button 
          variant="contained" 
          color="primary" 
          sx={{ mt: 2 }}
          onClick={() => checkSubscription(true)}
        >
          Refresh Subscription Status
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Look Up User
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="User ID"
            variant="outlined"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            fullWidth
          />
          <Button 
            variant="contained" 
            onClick={() => fetchUserData(userId)}
            disabled={loading}
          >
            Fetch Data
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {userData && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Data
                  </Typography>
                  <Box component="pre" sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper', 
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem'
                  }}>
                    {JSON.stringify(userData, null, 2)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionDebugger; 